package co.maytes.standupbot.dashboard.pages.legal

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.ui.Alignment
import co.maytes.standupbot.dashboard.components.layout.LegalBullets
import co.maytes.standupbot.dashboard.components.layout.LegalLayout
import co.maytes.standupbot.dashboard.components.layout.LegalSection
import co.maytes.standupbot.dashboard.components.layout.MarketingShell
import co.maytes.standupbot.dashboard.components.motion.FadeInOnMount
import co.maytes.standupbot.dashboard.components.widgets.TocItem
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.navigation.Link
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page("/legal/terms")
@Composable
fun TermsPage() {
    MarketingShell {
        FadeInOnMount(delayMs = 0, contentAlignment = Alignment.TopCenter) {
            LegalLayout(
                title = "Terms of Service",
                lastUpdated = "2026-04-21",
                summary = "You install on Slack, we run the bot; you can cancel with 30 days' notice, we work " +
                    "to resolve outages quickly, and we don't try to own your standups. Governing law: " +
                    "New South Wales.",
                toc = tocItems(),
            ) {
                SectionAgreement()
                SectionEligibility()
                SectionAcceptableUse()
                SectionYourData()
                SectionBilling()
                SectionCancellation()
                SectionUptime()
                SectionLiability()
                SectionGoverningLaw()
                SectionContact()
            }
        }
    }
}

private fun tocItems(): List<TocItem> = listOf(
    TocItem("agreement", "Agreement"),
    TocItem("eligibility", "Eligibility"),
    TocItem("acceptable-use", "Acceptable use"),
    TocItem("your-data", "Your data"),
    TocItem("billing", "Fees & billing"),
    TocItem("cancellation", "Cancellation"),
    TocItem("uptime", "Uptime"),
    TocItem("liability", "Liability"),
    TocItem("governing-law", "Governing law"),
    TocItem("contact", "Contact"),
)

@Composable
private fun SectionAgreement() {
    LegalSection(
        index = 1,
        anchor = "agreement",
        title = "Agreement",
        body = "By installing StandupBot on your Slack workspace, you agree to these terms on behalf of that " +
            "workspace. If you don't have authority to bind the workspace, install it somewhere else first.",
    )
}

@Composable
private fun SectionEligibility() {
    LegalSection(
        index = 2,
        anchor = "eligibility",
        title = "Account eligibility",
        body = "You need to be at least 18, have a valid Slack workspace you can administer, and a Linear " +
            "workspace you can grant us a read-only OAuth scope on. We don't support personal Slack " +
            "accounts or Linear team seats you don't actually have access to.",
    )
}

@Composable
private fun SectionAcceptableUse() {
    LegalSection(
        index = 3,
        anchor = "acceptable-use",
        title = "Acceptable use",
        body = "You agree not to:",
    ) {
        LegalBullets(
            listOf(
                "Scrape, mirror, or otherwise exfiltrate another workspace's standups or activity feed.",
                "Abuse the AI synthesis endpoint, stuffing prompts with unrelated content, running automated loops to burn tokens, or using the output to train a competing model.",
                "Resell, white-label, or rebrand StandupBot without a written agreement.",
                "Use the service for anything illegal, including unlawful surveillance of your own team.",
            ),
        )
    }
}

@Composable
private fun SectionYourData() {
    LegalSection(
        index = 4,
        anchor = "your-data",
        title = "Your data",
    ) {
        P(attrs = { classes("mk-legal-body") }) {
            Text(
                "The standups, Linear activity, and Slack metadata in your workspace belong to you. " +
                    "Maytes is a data processor. We store and operate on your data to deliver the service, " +
                    "and we don't claim any ownership rights over it. For what we actually do with the data, " +
                    "see our ",
            )
            Link(path = "/legal/privacy", text = "Privacy Policy", modifier = Modifier.classNames("mk-legal-link"))
            Text(".")
        }
    }
}

@Composable
private fun SectionBilling() {
    LegalSection(
        index = 5,
        anchor = "billing",
        title = "Fees & billing",
        body = "Plans are priced in US dollars on either a monthly or annual cycle. Subscriptions auto-renew " +
            "at the end of each cycle unless you cancel. We use Stripe to process cards and store billing " +
            "details, we do not see or store your card number. Taxes (GST for Australian customers, VAT " +
            "where applicable) are added at checkout.",
    )
}

@Composable
private fun SectionCancellation() {
    LegalSection(
        index = 6,
        anchor = "cancellation",
        title = "Cancellation",
        body = "Either side can cancel with 30 days' written notice, you by clicking Cancel in workspace " +
            "settings, us by emailing the admin on file. On cancellation, the service keeps working until " +
            "the end of your paid period. A workspace owner can export your data from Settings, or ask us to, up to " +
            "30 days after the account closes; after that, it's permanently deleted.",
    )
}

@Composable
private fun SectionUptime() {
    LegalSection(
        index = 7,
        anchor = "uptime",
        title = "Uptime",
        body = "We aim for 99.5% monthly uptime for standup delivery and the dashboard, though on our current " +
            "single-region setup we don't offer this as a contractual guarantee. If a prolonged outage disrupts " +
            "your standups, reach out, we'll work with you on a fair service credit. Scheduled maintenance " +
            "(announced 72 hours ahead) doesn't count.",
    )
}

@Composable
private fun SectionLiability() {
    LegalSection(
        index = 8,
        anchor = "liability",
        title = "Liability",
        body = "To the extent the law allows, Maytes's total liability under these terms is capped at the " +
            "amount you paid us in the 12 months before the claim. We're not liable for indirect, " +
            "incidental, or consequential damages, lost profits, lost standups, your team forgetting what " +
            "they did yesterday, and so on.",
    )
}

@Composable
private fun SectionGoverningLaw() {
    LegalSection(
        index = 9,
        anchor = "governing-law",
        title = "Governing law",
        body = "These terms are governed by the laws of New South Wales, Australia. Any dispute that can't " +
            "be resolved via good-faith conversation goes to the courts of Sydney, NSW. Arbitration clauses " +
            "and class-action waivers don't apply, we prefer conversations over paperwork.",
    )
}

@Composable
private fun SectionContact() {
    LegalSection(
        index = 10,
        anchor = "contact",
        title = "Contact",
        body = "Legal questions, takedown requests, or requests to amend these terms:",
    ) {
        P(attrs = { classes("mk-legal-body") }) {
            Text("Email ")
            Link(path = "mailto:legal@standupbot.app", text = "legal@standupbot.app", modifier = Modifier.classNames("mk-legal-link"))
        }
    }
}
